import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";
import { initAnalytics, trackPageView } from "@/lib/analytics";

import PublicLayout from "@/components/layout/PublicLayout";
import AdminShell from "@/components/layout/AdminShell";

import Home from "@/pages/public/Home";
import ExploreGhana from "@/pages/public/ExploreGhana";
import MarketPage from "@/pages/public/MarketPage";
import Opportunities from "@/pages/public/Opportunities";
import OpportunityDetail from "@/pages/public/OpportunityDetail";
import LearningCenter from "@/pages/public/LearningCenter";
import ArticleDetail from "@/pages/public/ArticleDetail";
import Resources from "@/pages/public/Resources";
import ResourceDetail from "@/pages/public/ResourceDetail";
import Terms from "@/pages/public/Terms";
import TglTerms from "@/pages/public/TglTerms";
import Consultation from "@/pages/public/Consultation";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import GlobalLinkApply from "@/pages/public/GlobalLinkApply";

import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminLeads from "@/pages/admin/Leads";
import AdminLeadDetail from "@/pages/admin/LeadDetail";
import AdminTasks from "@/pages/admin/Tasks";
import AdminConsultations from "@/pages/admin/Consultations";
import AdminOpportunities from "@/pages/admin/Opportunities";
import OpportunityEditor from "@/pages/admin/OpportunityEditor";
import AdminSettings from "@/pages/admin/Settings";
import AdminPartners from "@/pages/admin/Partners";
import AdminGlobalLink from "@/pages/admin/GlobalLink";
import AdminCountryIntel from "@/pages/admin/CountryIntel";
import AdminContent from "@/pages/admin/Content";
import AdminResources from "@/pages/admin/Resources";
import AdminMedia from "@/pages/admin/Media";
import AuriCommand from "@/pages/admin/AuriCommand";
import VoiceCommand from "@/pages/admin/VoiceCommand";
import AdminSecurityLog from "@/pages/admin/SecurityLog";
import AdminAnalytics from "@/pages/admin/Analytics";

import "@/App.css";

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <AnalyticsBoot />
          <Toaster position="bottom-center" richColors closeButton />
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/explore-ghana" element={<ExploreGhana />} />
              <Route path="/markets" element={<Navigate to="/opportunities" replace />} />
              <Route path="/markets/:slug" element={<MarketPage />} />
              <Route path="/opportunities" element={<Opportunities />} />
              <Route path="/opportunities/:slug" element={<OpportunityDetail />} />
              <Route path="/learning-center" element={<LearningCenter />} />
              <Route path="/learning-center/:slug" element={<ArticleDetail />} />
              {/* AURI-friendly short-form alias — the concierge emits /learning/<slug> links. */}
              <Route path="/learning" element={<LearningCenter />} />
              <Route path="/learning/:slug" element={<ArticleDetail />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/resources/:slug" element={<ResourceDetail />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/tgl-terms" element={<TglTerms />} />
              <Route path="/consultation" element={<Consultation />} />
              <Route path="/book-consultation" element={<Consultation />} />
              <Route path="/schedule" element={<Consultation />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/global-link" element={<GlobalLinkApply />} />
            </Route>

            {/* Admin */}
            <Route path="/altura-command/login" element={<AdminLogin />} />
            <Route path="/altura-command" element={<AdminShell />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="leads/:id" element={<AdminLeadDetail />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="consultations" element={<AdminConsultations />} />
              <Route path="opportunities" element={<AdminOpportunities />} />
              <Route path="opportunities/:id" element={<OpportunityEditor />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="global-link" element={<AdminGlobalLink />} />
              <Route path="country-intel" element={<AdminCountryIntel />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="resources" element={<AdminResources />} />
              <Route path="auri" element={<AuriCommand />} />
              <Route path="voice" element={<VoiceCommand />} />
              <Route path="security" element={<AdminSecurityLog />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}

function AnalyticsBoot() {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    // Fire page_view on every SPA route change. GA4 config sets send_page_view=false.
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}
